import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import * as words from '../../../../services/language'
import { MessageService } from '../../../../services/message.service';
import { MasterService } from '../../../../services/master.service';
import { StoreService } from '../../../../services/store.service';
import { Subject } from "rxjs";
import { Subscription } from 'rxjs';
import { ago } from 'src/app/utils/date';
import { SAVE_DEBOUNCE_MILLIS } from 'src/app/constants/sale';

@Component({
    selector: 'pay-promises',
    templateUrl: './pay-promises.component.html',
    styleUrls: ['./pay-promises.component.scss'],
    standalone: false
})
export class PayPromisesComponent implements OnInit, OnDestroy {

    // **************************
    // ****** variables *********
    // **************************

    // Unique internal identity of the lot whose payment promises will be queried for.
    @Input() lotId: number = 0

    // Unique internal identity of the contact whose payment promises will be queried for, overrides the lotId.
    @Input() contactId: number = 0

    // Unique internal identity of the sale whose payment promises will be queried for, overrides the lotId and contactId.
    @Input() saleId: number = 0

    // Unique internal identity of the sale finance type to filter the queried payment promises with.
    @Input() financeTypeId: number = 0

    // Available payments for the given contact.
    @Input() pays: Object | null = null

    // A payment was taken so lets update our available payments and any suppporting displays.
    @Input() paymentTaken: Subject<Object>

    // Currently selected language
    @Input() language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // Word replacements for each language.
    @Input() words: Object = words.language

    // An additional key to use to identify this component when multiple could be on the same page.
    @Input() key: string = ''

    @Output() promised: EventEmitter<Object> = new EventEmitter()

    //
    protected promises: Array<any> = []

    // 
    protected insight: any = null

    // The sale whose payment promises will be edited.
    protected editSale: any = null

    //
    private records: number = 0

    //
    private page: number = 1

    // Indicates whether the data is currently loading.
    protected loading: boolean = false

    // Indicates whether any more data is available
    protected exhausted: boolean = false

    // The show all form group
    protected form: FormGroup | null = null

    // A handle to a payment taken subscription so we can unsubscribe later.
    private paymentTakenWatcher: any = null

    // Delay a promise refresh shortly in case multiple promises 
    // were added/modified. Ideally we only refresh once.
    private refreshWatcher: ReturnType<typeof setTimeout> = null

    private subscriptions: Subscription[] = []

    // Check to see if there is enough information to create a new promise
    get creatable(): boolean {
        if (!this.pays) {
            return false
        }

        let count = 0
        let total = 0

        const past = this.pays['past']
        if (past) {
            count += past.items.length
            total += past.total
        }

        const today = this.pays['today']
        if (today) {
            count += today.items.length
            total += today.total
        }

        return ((count > 0) && (total > 0))
    }

    // Get a list of payments that can have a promise created for them.
    get promisable(): Array<any> {
        if (!this.pays) {
            return []
        }

        let items = null

        const past = this.pays['past']
        if (past && past.items && (past.items.length > 0)) {
            items = past.items
        }

        const today = this.pays['today']
        if (!items && today && today.items && (today.items.length > 0)) {
            items = today.items
        }

        if (!items) {
            return []
        }

        // return one list entry per sale...
        const entries = []
        for (const item of items) {
            const found = entries.find((entry) => entry.id == item.id)
            if (!found) {
                const copy = JSON.parse(JSON.stringify(item))
                entries.push(copy)
                continue
            }

            found.amount += item.amount
        }

        return entries
    }

    constructor (private route: ActivatedRoute, private router: Router, private ms: MessageService, private master: MasterService, private store: StoreService, private fb: FormBuilder) {
        this.form = this.fb.group({
            // Show all promises, even closed ones (available for contact display only)
            all: new FormControl<boolean>(false, [])
        })
    }

    ngOnInit() {
        this.fetch()
        this.fetchInsight()

        this.subscriptions.push(
            this.form.get('all').valueChanges.subscribe(_ => {
                this.reset()
                this.fetch()
            })
        )

        if (this.paymentTaken) {
            this.subscriptions.push(
                this.paymentTaken.subscribe((payment) => {
                    this.reset()
                    this.fetch()
                    this.fetchInsight()
                })
            )
        }
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
        if (this.refreshWatcher) {
            clearTimeout(this.refreshWatcher)
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.lotId || changes.contactId || changes.saleId || changes.financeTypeId) {
            this.reset()
            this.fetch()
        }
    }

    private reset = (): void => {
        this.page = 1
        this.exhausted = false
        this.records = 0
    }

    private fetch = (): void => {
        if (this.loading) {
            return
        }

        if (this.exhausted) { // currently showing all available records?
            return
        }

        let closedParam = '&closed=false'

        let lotParam = ''
        if (this.lotId && (this.lotId > 0)) {
            lotParam = `&lot=${this.lotId}`
        }

        let contactParam = ''
        if (this.contactId && (this.contactId > 0)) {
            contactParam = `&contact=${this.contactId}`
            lotParam = ''

            if (this.form.get('all').value) {
                closedParam = ''
            }
        }

        let saleParam = ''
        if (this.saleId && (this.saleId > 0)) {
            saleParam = `&sale=${this.saleId}`
            contactParam = ''
            lotParam = ''

            if (this.form.get('all').value) {
                closedParam = ''
            }
        }

        let financeTypeParam = ''
        if (this.financeTypeId && (this.financeTypeId > 0)) {
            financeTypeParam = `&financeType=${this.financeTypeId}`
        }

        this.loading = true
        if (this.page === 1) {
            this.promises = []
        }

        this.master.get(`payments/promises?page=${this.page}&sort=due|ASC${lotParam}${contactParam}${saleParam}${financeTypeParam}${closedParam}`, (res) => {
            this.loading = false
  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
  
            this.records = res.data.records
            this.exhausted = (res.data.next === null)
  
            if (this.page > 1) {
                this.promises = this.promises.concat(res.data.payments)
                return
            }
  
            this.promises = res.data.promises
        })
    }

    private fetchInsight = (): void => {
        if (!this.contactId || (this.contactId <= 0)) {
            return
        }

        this.insight = null

        this.master.get(`insight/contacts/${this.contactId}/promises`, (res) => {
            this.loading = false
  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
  
            this.insight = res.data
        })
    }

    /**
     * A promise in the list was clicked. 
     * @param e Event meta data that triggered the selection.
     * @param promise The payment promise object that was chosen by the user.
     */
    protected selected = (e: Event, promise: Object): void => {
        e.preventDefault()
        e.stopPropagation()
        
        if (!promise || !promise['contactId'] || promise['closed'] || promise['broken']) {
            return
        }

        // this checks to see if we are on a customer collect screen or the main collections screen.
        if (!this.lotId && (this.lotId <= 0) && this.contactId && (this.contactId > 0)) {
            this.edit(e, promise)
            return
        }

        this.router.navigateByUrl(`/collections/${promise['contactId']}?sale=${promise['saleId']}`)
    }

    /**
     * Open a modal to accept information related to a new promise to pay.
     * @param e Event meta data that triggered the creation.
     * @param sale The sale that will have a promise created.
     */
    protected create = (e: Event, sale: Object): void => {
        if (!sale || !sale['id']) {
            return
        }

        const id = parseInt(sale['id'])
        if (!id || isNaN(id) || (id <= 0)) {
            return
        }

        this.editSale = sale

        const btn = document.getElementById(`togglePayPromiseEdit${this.key}`) as HTMLButtonElement
        if (!btn) {
            return
        }

        btn.click()
    }

    /**
     * Open a modal to accept information related an existing promise to pay.
     * @param e Event meta data that triggered the edit.
     * @param promise The promise that will be edited.
     */
    protected edit = (e: Event, promise: Object): void => {
        if (!promise || !promise['saleId']) {
            return
        }

        const id = parseInt(promise['saleId'])
        if (!id || isNaN(id) || (id <= 0)) {
            return
        }

        this.editSale = this.promisable.find((item) => item.id == id)
        if (!this.editSale) {
            if (!this.pays) {
                return
            }
            
            const next = this.pays['next']
            if (!next || !next.items || (next.items.length <= 0)) {
                return   
            }

            const found = next.items.find((item) => item.id == id)
            if (!found) {
                return
            }

            this.editSale = found
        }

        const btn = document.getElementById(`togglePayPromiseEdit${this.key}`) as HTMLButtonElement
        if (!btn) {
            return
        }

        btn.click()
    }

     /**
     * Determine an tab icon to show based on the payment category.
     * @param category The payment category.
     */
     protected icon = (category: string) => {
        if (!category || (category.length <= 0)) {
            return 'monetization_on'
        }

        switch(category.toLowerCase()) {
            case 'mv loan':
                return 'directions_car'
            case 'service':
                return 'car_repair'
            default:
                return 'monetization_on'
        }
    }

    protected dueToClass = (due: string): string => {
        return ago(due)
    }

    /**
     * Listen for payment processing events from the payment promises component then update 
     * the display and pass the changes to other components.
     * @param changed Indicates one or more payment promises were added or modified.
     */
    protected promisesEdited = (changed: boolean) => {
        const btn = document.getElementById(`togglePayPromiseEdit${this.key}`) as HTMLButtonElement
        if (btn) {
            btn.click()
        }

        this.editSale = null

        if (!changed) {
            return
        }

        if (this.refreshWatcher) {
            clearTimeout(this.refreshWatcher)
        }

        this.refreshWatcher = setTimeout(() => {
            this.reset()
            this.fetch()
            this.fetchInsight()
            this.promised.emit({changed: changed})
        }, SAVE_DEBOUNCE_MILLIS)
    }
}
